import first from "lodash/first";
import { useRouter } from "next/router";
import type { FunctionComponentWithRef, MouseEvent } from "react";
import { useCallback, useMemo } from "react";
import { graphql, useFragment } from "react-relay";

import { notEmpty } from "shared/lib/utils";

import { checkIsPlusArticle } from "scmp-app/components/article/article-render/plus/helpers";
import { withHighlightedHeadline } from "scmp-app/components/content/content-item-render/highlight/hoc";
import { useContentItemProviders } from "scmp-app/components/content/content-item-render/hooks";
import type { ContentItemRenderVariantProps } from "scmp-app/components/content/content-item-render/types";
import { getContentSponsor } from "scmp-app/components/content/content-sponsor-tag/helpers";
import { usePlusArticlePopup } from "scmp-app/components/plus/article-popup/hooks";
import { useSearchContext } from "scmp-app/components/search/contexts";
import { sendGA4Tracking } from "scmp-app/components/tracking/google-analytics-4/apis";
import type { SearchEvent } from "scmp-app/components/tracking/google-analytics-4/types";
import { pathname as plusSearchPathname } from "scmp-app/pages/plus/search";
import type { searchContentItemContent$key } from "scmp-app/queries/__generated__/searchContentItemContent.graphql";

import {
  Author,
  Container,
  CoverImage,
  Header,
  Headline,
  PlusFlag,
  PrintHeadline,
  PrintHeadlineContainer,
  PrintHeadlineHeader,
  StyledEntityLink,
  StyledPublishedDate,
  StyledSections,
  Summary,
} from "./styles";

type Props = ContentItemRenderVariantProps & {
  reference: searchContentItemContent$key;
};

const Component: FunctionComponentWithRef<Props, HTMLDivElement> = ({
  className,
  ref,
  reference,
}) => {
  const content = useFragment(
    graphql`
      fragment searchContentItemContent on Content {
        __typename
        entityId
        authors {
          entityId
        }
        topics {
          entityId
        }
        sectionsV2 {
          value {
            ... on Section {
              entityId
            }
            ... on VideoSection {
              entityId
            }
          }
        }
        ...contentPublishedDateContent
        ...contentSectionsContent
        ...entityLink
        ...helpersCheckCanShowInPlusPlatformContent
        ...helpersCheckIsPlusArticle
        ...helpersUseContentSponsorContent
        ...hooksPlusArticleLinkHandlerArticle
        ...hooksContentItemProvidersContent
          @arguments(
            withAuthorLink: true
            withCoverImage: true
            withCoverImageSize118x118: true
            withCoverImageSize540x360: true
            withGalleryOverlay: true
            withHeadline: true
            withPrintHeadline: true
            withSponsorTag: true
            withSummary: true
            withVideoDurationOverlay: true
          )
      }
    `,
    reference,
  );

  const providers = useContentItemProviders(content);
  const { route } = useRouter();

  const isOnPlusSearch = useMemo(() => route === plusSearchPathname, [route]);
  const needToShowPlusFlag = useMemo(() => checkIsPlusArticle(content), [content]);

  const contentSponsor = getContentSponsor(content);
  const isSponsorArticle = notEmpty(contentSponsor?.name);

  const { articleResultIdMap, currentValue, refineSearch } = useSearchContext();

  const byline = useMemo(() => {
    if (isSponsorArticle) {
      return providers.sponsorTag?.({
        color: "#001246",
        gap: "4px",
        layoutDirection: "horizontal",
        nameFontSize: "12px",
        typeFontSize: "12px",
      });
    }

    return providers.author({
      ignoreAuthorType: true,
      isMultiple: true,
      onClick: (_entityUuid: string, entityId?: string) => {
        const resultId = articleResultIdMap?.get(content.entityId)?.resultId ?? "";
        // GA4 21.2.1
        sendGA4Tracking({
          action: "click",
          category: "search",
          customized_parameters: {
            author_id: entityId,
            is_plus_article: needToShowPlusFlag ? "yes" : "no",
            search_id: resultId,
            search_text: currentValue,
          },
          subcategory: "result",
        });
      },
    });
  }, [
    articleResultIdMap,
    content.entityId,
    currentValue,
    isSponsorArticle,
    needToShowPlusFlag,
    providers,
  ]);

  const listOfSectionIds =
    first(content.sectionsV2)
      ?.value?.map(section => section?.entityId)
      .join(",") ?? "";

  const listOfTopicIds = content.topics?.map(topic => topic?.entityId).join(",") ?? "";

  const { handleOpen } = usePlusArticlePopup();

  const handleItemClick = useCallback(
    (event: MouseEvent) => {
      const page = articleResultIdMap?.get(content.entityId)?.page;
      const position = articleResultIdMap?.get(content.entityId)?.position;
      const resultId = articleResultIdMap?.get(content.entityId)?.resultId;

      const getOrderBy = () => {
        if (refineSearch.orderBy?.field === "PUBLISHED_DATE") {
          switch (refineSearch.orderBy.direction) {
            case "ASC":
              return "oldest";
            case "DESC":
              return "newest";
          }
        }
        return "relevance";
      };

      const getSection = () => {
        if (!notEmpty(refineSearch.sectionIds)) return "All";
        return refineSearch.sectionIds.join(",");
      };

      // GA4 21.2.1
      sendGA4Tracking({
        action: "click",
        category: "search",
        customized_parameters: {
          article_id: content?.entityId,
          article_type: refineSearch.articleTypeText,
          author_id: first(content?.authors)?.entityId,
          date_range: refineSearch.dateRangeText,
          is_plus_article: needToShowPlusFlag ? "yes" : "no",
          page,
          position,
          search_id: resultId,
          search_text: currentValue,
          section: getSection(),
          section_id: listOfSectionIds,
          sort: getOrderBy(),
          topic_id: listOfTopicIds,
        },
        subcategory: "result",
      } as SearchEvent);

      if (isOnPlusSearch) {
        event.preventDefault();
        handleOpen(content);
      }
    },
    [
      articleResultIdMap,
      content,
      currentValue,
      handleOpen,
      isOnPlusSearch,
      listOfSectionIds,
      listOfTopicIds,
      needToShowPlusFlag,
      refineSearch,
    ],
  );

  return (
    <Container className={className} ref={ref}>
      <StyledPublishedDate format="dd MMM yyyy" reference={content} />

      <Header>
        <StyledSections reference={content} />
        {needToShowPlusFlag && <PlusFlag aria-label="Plus" role="img" />}
      </Header>

      <Headline>
        <StyledEntityLink onClick={handleItemClick} reference={content} target="_blank">
          {providers.headline()}
        </StyledEntityLink>
      </Headline>

      {!isSponsorArticle && (
        <Summary>
          <StyledEntityLink onClick={handleItemClick} reference={content} target="_blank">
            {providers.summary()}
          </StyledEntityLink>
        </Summary>
      )}

      <Author>{byline}</Author>

      <PrintHeadlineContainer>
        <PrintHeadlineHeader>Appeared in print as:</PrintHeadlineHeader>
        <PrintHeadline>{providers.printHeadline()}</PrintHeadline>
      </PrintHeadlineContainer>

      <CoverImage>
        <StyledEntityLink onClick={handleItemClick} reference={content} target="_blank">
          {providers.coverImage({
            responsiveVariants: {
              desktopUp: "size540x360",
              mobileUp: "size118x118",
              tabletUp: "size540x360",
            },
          })}
          {content.__typename === "Video" &&
            providers.video?.durationOverlay({
              iconSize: "small",
              variant: "homepage",
            })}
          {content.__typename === "Gallery" && providers.gallery?.indicator({ withValue: false })}
        </StyledEntityLink>
      </CoverImage>
    </Container>
  );
};

Component.displayName = "ContentItemSearch";

export const ContentItemSearch = withHighlightedHeadline(Component);
